import type { ElementType, HTMLAttributes, ReactNode } from 'react'

interface TypographyProps extends HTMLAttributes<HTMLElement> {
  children?: ReactNode
  /** Render as a different HTML element while keeping the same typography styles */
  as?: ElementType
  /** Forwarded to the underlying element when as="label" */
  htmlFor?: string
}

// Heading-01 · Merriweather Bold · 80px / 80px
export function H1({ className = '', children, ...props }: TypographyProps) {
  return (
    <h1
      className={`font-display break-before-auto text-[3rem] leading-tight font-bold md:text-[5rem] md:leading-20 ${className}`}
      {...props}
    >
      {children}
    </h1>
  )
}

// Heading-02 · Atkinson Bold · 50px / 60px
export function H2({ className = '', children, ...props }: TypographyProps) {
  return (
    <h2
      className={`text-[3.125rem] leading-15 font-bold ${className}`}
      {...props}
    >
      {children}
    </h2>
  )
}

// Heading-03 · Atkinson Bold · 24px / 32px
export function H3({ className = '', children, ...props }: TypographyProps) {
  return (
    <h3
      className={`text-2xl leading-8 font-bold md:text-[2rem] ${className}`}
      {...props}
    >
      {children}
    </h3>
  )
}

// Heading-04 · Atkinson · 24px / 32px · Bold (default) or Regular
export function H4({
  className = '',
  bold = true,
  children,
  ...props
}: TypographyProps & { bold?: boolean }) {
  return (
    <h4
      className={`text-2xl leading-8 ${bold ? 'font-bold' : 'font-normal'} ${className}`}
      {...props}
    >
      {children}
    </h4>
  )
}

// Heading-05 · Atkinson · 20px / 40px · Bold (default) or Regular
export function H5({
  className = '',
  bold = true,
  children,
  ...props
}: TypographyProps & { bold?: boolean }) {
  return (
    <h5
      className={`text-xl leading-10 ${bold ? 'font-bold' : 'font-normal'} ${className}`}
      {...props}
    >
      {children}
    </h5>
  )
}

// Body 01 · Atkinson Regular · 16px / 24px · tracking 0.16px
export function Text({
  className = '',
  as: Tag = 'p',
  children,
  ...props
}: TypographyProps) {
  return (
    <Tag
      className={`text-base leading-6 tracking-[0.16px] ${className}`}
      {...props}
    >
      {children}
    </Tag>
  )
}

// Body 02 · Atkinson Regular · 18px / 24px · tracking 0.16px
export function TextLg({
  className = '',
  children,
  ...props
}: TypographyProps) {
  return (
    <p
      className={`text-[1.125rem] leading-6 tracking-[0.16px] ${className}`}
      {...props}
    >
      {children}
    </p>
  )
}

// Body 03 · Atkinson Regular · 14px / 24px
export function Span({
  className = '',
  as: Tag = 'span',
  children,
  textSize = 'text-sm leading-6',
  ...props
}: TypographyProps & { textSize?: string }) {
  return (
    <Tag className={textSize + ' ' + className} {...props}>
      {children}
    </Tag>
  )
}

// Terms & Captions · Atkinson Regular · 10px / 12px · tracking 0.32px
export function Caption({
  className = '',
  children,
  ...props
}: TypographyProps) {
  return (
    <p
      className={`tracking-base text-[0.625rem] leading-3 ${className}`}
      {...props}
    >
      {children}
    </p>
  )
}
